/**
 * Utility functions for NotificationRecipientsSelectorDialog component
 */

import { CategorySelection, CategoryAndTagsData, ContactGroupPayload, EmailSubscriberSelection } from '../types/NotificationRecipient';

// Constants
export const DEFAULT_UUID = '497f6eca-6276-4993-bfeb-53cbbbba6f08';
export const DEFAULT_GROUP_NAME = 'Contact Group';
export const DEFAULT_CREATED_BY = '1';

/**
 * Builds category and tags data from selected categories
 * @param selectedCategoryTags - Selected category tags mapping
 * @param categories - Available categories
 * @param orgUuid - Organization UUID
 * @returns Array of CategoryAndTagsData
 */
export const buildCategoryAndTagsData = (
  selectedCategoryTags: CategorySelection[],
  categories: any[],
  orgUuid: string
): CategoryAndTagsData[] => {
  const categoryAndTagsData: CategoryAndTagsData[] = [];

  for (const group of selectedCategoryTags) {
    for (const categoryId in group) {
      if (!Object.prototype.hasOwnProperty.call(group, categoryId)) continue;

      const category = categories.find((cate) => cate.id === parseInt(categoryId, 10));
      
      if (!category) {
        console.warn(`Category with id ${categoryId} not found`);
        continue;
      }

      const temp: CategoryAndTagsData = {
        selectedCategory: {
          categoryID: category.id,
          name: category.name,
          fkOrgID: orgUuid,
          catuuid: category.uuid,
        },
        selectedTags: group[categoryId].map((tag) => ({
          tagId: tag.value,
          name: tag.label,
          fkOrgID: orgUuid,
          tagcategoryId: category.id,
          taguuid: DEFAULT_UUID,
          useruuid: DEFAULT_UUID,
        })),
      };

      categoryAndTagsData.push(temp);
    }
  }

  return categoryAndTagsData;
};

/**
 * Builds flat tag array from selected category tags
 * @param selectedCategoryTags - Selected category tags mapping
 * @returns Array of tag values
 */
export const buildFlatTagArray = (selectedCategoryTags: CategorySelection[]): string[] => {
  const flatTags: string[] = [];

  selectedCategoryTags.forEach((categoryGroup) => {
    for (const categoryId in categoryGroup) {
      if (Object.prototype.hasOwnProperty.call(categoryGroup, categoryId)) {
        categoryGroup[categoryId].forEach((tag) => {
          flatTags.push(tag.value);
        });
      }
    }
  });

  return flatTags;
};

/**
 * Validates if user has selected any recipients
 * @param params - Validation parameters
 * @returns true if at least one selection is made
 */
export const isValidRecipientSelection = (params: {
  isAllSchoolLinkAppUsersSelected: boolean;
  isAllEmailOnlySubscribersSelected: boolean;
  selectedUserId: string[];
  selectedCategoryTags: CategorySelection[];
  selectedEmailOnlySubscribers: EmailSubscriberSelection[];
}): boolean => {
  const {
    isAllSchoolLinkAppUsersSelected,
    isAllEmailOnlySubscribersSelected,
    selectedUserId,
    selectedCategoryTags,
    selectedEmailOnlySubscribers,
  } = params;

  return (
    isAllSchoolLinkAppUsersSelected ||
    isAllEmailOnlySubscribersSelected ||
    selectedUserId.length > 0 ||
    selectedCategoryTags.length > 0 ||
    selectedEmailOnlySubscribers.length > 0
  );
};

/**
 * Builds contact group payload
 * @param params - Payload parameters
 * @returns ContactGroupPayload
 */
export const buildContactGroupPayload = (params: {
  selectedUserId: string[];
  selectedCategoryTags: CategorySelection[];
  selectedEmailOnlySubscribers: EmailSubscriberSelection[];
  isAllSchoolLinkAppUsersSelected: boolean;
  isAllEmailOnlySubscribersSelected: boolean;
  categories: any[];
  orgUuid: string;
  epubOrgId?: string;
  channels?: string[] | string;
  previewedRecipientData?: any[];
}): ContactGroupPayload => {
  const {
    selectedUserId,
    selectedCategoryTags,
    selectedEmailOnlySubscribers,
    isAllSchoolLinkAppUsersSelected,
    isAllEmailOnlySubscribersSelected,
    categories,
    orgUuid,
    epubOrgId,
    channels,
    previewedRecipientData,
  } = params;

  const flatTags = buildFlatTagArray(selectedCategoryTags);
  const categoryAndTagsData = buildCategoryAndTagsData(selectedCategoryTags, categories, orgUuid);

  const payload: ContactGroupPayload = {
    name: DEFAULT_GROUP_NAME,
    selectedUsers: selectedUserId,
    selectedTags: flatTags,
    selectedCategoriesAndTags: categoryAndTagsData,
    createdBy: DEFAULT_CREATED_BY,
    emailOnlySubscribers: selectedEmailOnlySubscribers,
    isAllSchoolLinkAppUsersSelected,
    isAllEmailOnlySubscribersSelected,
  };

  if (epubOrgId) {
    payload.epubOrgId = epubOrgId;
  }

  if (orgUuid) {
    payload.fkOrgID = orgUuid;
  }

  if (channels) {
    payload.selectedChanels = Array.isArray(channels) ? channels.join(',') : channels;
  }

  if (previewedRecipientData) {
    payload.previewedRecipientData = previewedRecipientData;
  }

  return payload;
};

/**
 * Convert string boolean to actual boolean
 * @param value - String value "true" or "false"
 * @returns Boolean value
 */
export const stringToBool = (value: string | boolean): boolean => {
  if (typeof value === 'boolean') return value;
  return value.toLowerCase() === 'true';
};

/**
 * Safely get category by id
 * @param categoryId - Category ID
 * @param categories - Categories array
 * @returns Category or null
 */
export const getCategoryById = (categoryId: number, categories: any[]): any | null => {
  const category = categories.find((cate) => cate.id === categoryId);
  return category || null;
};
